import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { MessageCircle, Send, CalendarDays, Ruler, Sparkles } from "lucide-react";

const CHAT = [
  ["user", "How big is the kitchen?"],
  ["bot", "The Kitchen & Family Room is 21'0\" × 13'9\", with island seating and doors to the backyard."],
  ["user", "What would quartz countertops cost?"],
  ["bot", "Calacatta quartz is a +$3,200 upgrade. Your total would be $628,200 before GST. Want me to add it to your saved home?"],
  ["user", "Is the basement finished?"],
  ["bot", "I don't have that detail. Would you like to book a viewing so our sales team can walk you through it?"],
];

function ChatMock() {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) { setN(0); return undefined; }
    const t = setInterval(() => setN((v) => (v >= CHAT.length + 2 ? 1 : v + 1)), 1500);
    return () => clearInterval(t);
  }, [inView]);
  const shown = CHAT.slice(0, Math.min(n, CHAT.length));
  return (
    <div ref={ref} className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl aspect-[4/3] w-full" data-testid="chat-showcase-mock">
      <img src="/seed/kit1.jpg" alt="Inside a 3D tour" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#071526]/70 via-transparent to-transparent" />
      <span className="absolute left-[22%] top-[48%] w-11 h-11 rounded-full bg-white/95 grid place-items-center ring-4 ring-white/30 shadow-xl"><Ruler className="w-5 h-5 text-[#0F2A47]" /></span>
      <span className="absolute left-[30%] top-[46%] hidden sm:inline-block rounded-full bg-[#071526]/80 text-white text-xs px-3 py-1">Kitchen · 21'0" × 13'9"</span>
      <span className="absolute left-[55%] top-[62%] w-11 h-11 rounded-full bg-white/95 grid place-items-center ring-4 ring-white/30 shadow-xl"><Sparkles className="w-5 h-5 text-[#D4A12A]" /></span>
      <div className="absolute right-3 bottom-3 top-3 sm:right-5 sm:bottom-5 sm:top-5 w-[68%] sm:w-[52%] rounded-2xl bg-white text-slate-800 flex flex-col overflow-hidden shadow-2xl">
        <div className="bg-[#0F2A47] text-white px-4 py-3"><p className="font-serif text-sm sm:text-base leading-tight">Home assistant</p><p className="text-[10px] text-white/60">Answers from your home data</p></div>
        <div className="flex-1 p-3 space-y-2 overflow-hidden flex flex-col justify-end bg-[#FAF8F5]">
          {shown.slice(-4).map(([who, text], i) => (
            <motion.div key={`${n}-${i}-${text}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
              className={`max-w-[90%] rounded-2xl px-3 py-2 text-[11px] sm:text-xs leading-snug ${who === "user" ? "ml-auto bg-[#0F2A47] text-white" : "bg-white border"}`}>{text}</motion.div>
          ))}
        </div>
        <div className="px-3 pt-1 text-[10px] font-semibold text-[#0F2A47] flex items-center gap-1"><CalendarDays className="w-3 h-3" />Book a viewing</div>
        <div className="p-2 flex gap-2"><span className="flex-1 h-8 rounded-full border text-[10px] text-slate-400 px-3 flex items-center">Ask about this home…</span><span className="w-8 h-8 rounded-full bg-[#0F2A47] text-white grid place-items-center"><Send className="w-3 h-3" /></span></div>
      </div>
    </div>
  );
}

const POINTS = [
  "Answers only from your own data: room sizes, specs, finishes, upgrade prices, GST and the community",
  "Lives inside the 3D tour, so buyers ask while they look around",
  "Never guesses. If it doesn't know, it offers to book a viewing",
  "Every question is saved, so your team can see what buyers really want to know",
];

export function ChatShowcase() {
  return (
    <section className="relative z-10 bg-[#071526] text-white py-24 border-t border-white/5" data-testid="landing-chat-showcase">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.15fr] gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#E9C25A] flex items-center gap-2"><MessageCircle className="w-4 h-4" />AI home assistant</p>
          <h2 className="font-serif text-3xl sm:text-5xl font-semibold mt-3 leading-tight">A sales assistant inside every tour, awake at 2am.</h2>
          <p className="text-white/60 mt-4 max-w-xl">Buyers tap "Ask about this home" while they walk through the Matterport tour and get instant answers in your brand's voice.</p>
          <ul className="mt-8 space-y-3">
            {POINTS.map((p, i) => (
              <li key={p} className="flex gap-3 text-sm text-white/80" data-testid={`chat-point-${i}`}><span className="mt-1.5 w-2 h-2 rounded-full bg-[#D4A12A] shrink-0" />{p}</li>
            ))}
          </ul>
        </div>
        <ChatMock />
      </div>
    </section>
  );
}
